/**
 * The objects one owner-onboarding invite covers.
 *
 * An invite used to name exactly one property, so every step of
 * `/onboarding/:token` read `property` and `tokenData.prefilled_rooms`
 * directly. A grouped invite (20261004200000) can carry up to 15, which the
 * owner signs for in a single pass.
 *
 * Rather than branching every step on "one or many", the page normalises both
 * shapes into ONE list. A legacy single-object invite is a list of length 1 and
 * renders exactly as it did before; nothing downstream has to know which shape
 * it came from.
 *
 * Money note: `feePerRoom` is NET per room, matching
 * `owner_onboarding_tokens.prefilled_fee_amount` and the page's existing
 * `getVatTotal(net) = net * 1.19`. Everything here stays in net; VAT is applied
 * once, at the point of display.
 */

/** Hard cap from the schema: the MULTI Vollmacht prints a 3x5 grid. */
export const MAX_ONBOARDING_OBJECTS = 15;

/**
 * Net management fee per room per month, applied when an invite (legacy or
 * grouped) has no admin-configured fee for an object, rather than silently
 * billing 0. Neel, 2026-10-16: raised from 33.
 *
 * Exported from here (and mirrored at
 * supabase/functions/_shared/owner-order-objects.ts, asserted equal by
 * owner-order-objects.parity.test.ts) rather than redefined as a literal at
 * each call site -- the whole point of this module pair is to keep the
 * frontend and the server pricing the same invite identically, and a
 * default that drifted between them would be exactly the kind of mismatch
 * it exists to prevent.
 */
export const DEFAULT_MANAGEMENT_FEE = 49;

export interface OnboardingObject {
  /** Printed slot 1..15 on the MULTI Vollmacht. 1 for a legacy invite. */
  slot: number;
  propertyId: number | null;
  apartmentId: number | null;
  title: string | null;
  /** Display address, already composed. */
  address: string | null;
  city: string | null;
  zipCode: string | null;
  rooms: number | null;
  livingArea: number | null;
  /**
   * Billing status. `isVacant` reads THIS, so a value here zero-rates the
   * object. Grouped invites leave it null on purpose -- see
   * objectsFromGroupedRows.
   */
  tenancyStatus: string | null;
  /**
   * The letting status shown on the review screen. Separate from
   * `tenancyStatus` so putting a real status in front of the owner cannot
   * change what they are charged.
   */
  displayStatus?: string | null;
  /** Net fee per room per month. */
  feePerRoom: number | null;
}

/** A row from `get_owner_onboarding_properties`. */
export interface GroupedObjectRow {
  slot: number;
  property_id: number | null;
  apartment_id: number | null;
  prefilled_property_name: string | null;
  prefilled_property_address: string | null;
  prefilled_rooms: number | null;
  prefilled_fee_amount: number | null;
  /**
   * Derived live from the apartment by get_owner_onboarding_properties, not
   * snapshotted onto the invite like the fields above. Optional so a client
   * reading an older deployment of the RPC still type-checks.
   */
  living_area?: number | null;
  tenancy_status?: string | null;
}

/** The shape `properties` is selected into on the onboarding page. */
export interface LegacyPropertyRow {
  id: string | number;
  title: string | null;
  address: string | null;
  city: string | null;
  zip_code: string | null;
  rooms: number | null;
  living_area: number | null;
  tenancy_status: string | null;
}

/**
 * Vacancy is free, per object — not portfolio-wide. An owner handing over one
 * empty building alongside two full ones still pays for the two.
 *
 * `tenancy_status` is free text, so this matches the vacant spellings the admin
 * surfaces write and treats anything else (including null) as billable: a
 * missing status must never silently zero a real invoice line.
 */
const VACANT_STATUSES = new Set(["vacant", "leerstand", "leer", "unrented"]);

export function isVacant(obj: OnboardingObject): boolean {
  const status = obj.tenancyStatus?.trim().toLowerCase();
  return status ? VACANT_STATUSES.has(status) : false;
}

/** Net monthly fee for one object. Vacancy bills nothing. */
export function objectNetFee(obj: OnboardingObject): number {
  if (isVacant(obj)) return 0;
  if (obj.feePerRoom == null) return 0;
  return obj.feePerRoom * (obj.rooms ?? 0);
}

/** Mirror of `isOrderPriceable` in the edge functions' shared module. */
export function isOrderPriceable(objects: OnboardingObject[]): boolean {
  return (
    objects.length > 0 &&
    objects.every(
      (o) => isVacant(o) || (Number.isInteger(o.rooms) && (o.rooms as number) > 0)
    )
  );
}

/** Net monthly fee across the whole invite. */
export function totalNetFee(objects: OnboardingObject[]): number {
  return objects.reduce((sum, obj) => sum + objectNetFee(obj), 0);
}

/** Rooms across the whole invite, vacant ones included — they are still rooms. */
export function totalRooms(objects: OnboardingObject[]): number {
  return objects.reduce((sum, obj) => sum + (obj.rooms ?? 0), 0);
}

/**
 * The one net per-room rate this invite is priced at, or null when its objects
 * disagree.
 *
 * The headline "X € / Zimmer / Monat" is only true when every billable object
 * shares a rate. The fee lives on the apartment, so a grouped invite can
 * legitimately mix them — and then a single headline figure would misstate the
 * price. Vacant objects are ignored: they are billed at nothing, so their rate
 * says nothing about what the owner pays.
 */
export function uniformFeePerRoom(objects: OnboardingObject[]): number | null {
  const rates = new Set(
    objects
      .filter((o) => !isVacant(o) && o.feePerRoom != null)
      .map((o) => o.feePerRoom as number)
  );
  return rates.size === 1 ? [...rates][0] : null;
}

/** Composes "Street 1, 12345 City" from whatever parts are present. */
export function composeObjectAddress(parts: {
  address: string | null;
  zipCode?: string | null;
  city?: string | null;
}): string | null {
  const tail = [parts.zipCode, parts.city].filter(Boolean).join(" ").trim();
  const head = parts.address?.trim() || null;
  if (head && tail) return `${head}, ${tail}`;
  return head || tail || null;
}

/**
 * Normalises a grouped invite into the shared list.
 *
 * Rows arrive in slot order from the lookup, but the order is re-asserted here:
 * it decides which printed slot each object occupies on the Vollmacht, and that
 * must not depend on the transport preserving it.
 */
export function objectsFromGroupedRows(
  rows: GroupedObjectRow[],
  /** Neel, 2026-10-16: a grouped row with no admin-configured fee used to
      silently bill €0 for that object -- the legacy single-property path
      already falls back to a default rate, but this one never received it.
      Optional only so existing call sites/tests that truly want "unset
      stays unset" don't have to pass one. */
  fallbackFeePerRoom?: number | null
): OnboardingObject[] {
  return [...rows]
    .sort((a, b) => a.slot - b.slot)
    .slice(0, MAX_ONBOARDING_OBJECTS)
    .map((row) => ({
      slot: row.slot,
      propertyId: row.property_id,
      apartmentId: row.apartment_id,
      title: row.prefilled_property_name,
      address: row.prefilled_property_address,
      city: null,
      zipCode: null,
      rooms: row.prefilled_rooms,
      // Derived live from the apartment by the RPC. Display only -- it has no
      // part in pricing, so reading it cannot move a number.
      livingArea: row.living_area ?? null,
      // The apartment's real letting status, for the review screen ONLY.
      //
      // Deliberately NOT written to `tenancyStatus` below: that field decides
      // whether an object bills (isVacant -> objectNetFee -> 0), and grouped
      // rows have always billed because they snapshot no status. Feeding live
      // data into it would silently zero-rate every vacant unit on a grouped
      // invite -- a pricing change, arriving through a change meant to put
      // three labels on a screen. Showing a status and charging for it is
      // correct here: the fee is for managing the unit, not for it being let.
      displayStatus: row.tenancy_status ?? null,
      // Grouped rows carry their own fee because it lives on the apartment, so
      // two objects in one invite can legitimately differ -- but a row that
      // never got one still needs the same default rate a legacy invite gets,
      // not a silent €0.
      feePerRoom: row.prefilled_fee_amount ?? fallbackFeePerRoom ?? null,
      // Grouped rows snapshot no tenancy status; billing a snapshot as vacant
      // would be guessing, so they bill.
      tenancyStatus: null,
    }));
}

/**
 * Normalises a legacy single-property invite into the same list.
 *
 * `fallbackRooms` is the token's `prefilled_rooms`, which the page has always
 * preferred over `properties.rooms` — it is what was agreed at invite time.
 */
export function objectsFromLegacyProperty(
  property: LegacyPropertyRow | null,
  fallbackRooms: number | null,
  feePerRoom: number | null
): OnboardingObject[] {
  if (!property) return [];
  return [
    {
      slot: 1,
      propertyId: Number(property.id),
      apartmentId: null,
      title: property.title,
      address: composeObjectAddress({
        address: property.address,
        zipCode: property.zip_code,
        city: property.city,
      }),
      city: property.city,
      zipCode: property.zip_code,
      rooms: fallbackRooms ?? property.rooms,
      livingArea: property.living_area,
      tenancyStatus: property.tenancy_status,
      // A legacy invite already bills on its own status, so display and
      // billing agree here; it is only the grouped shape that must not.
      displayStatus: property.tenancy_status,
      feePerRoom,
    },
  ];
}

/**
 * Round to whole cents.
 *
 * The page formats with toLocaleString (rounds half up) and both
 * Auftragsbestaetigung generators with toFixed (binary), which disagree on an
 * exact half-cent: a 69.50 EUR net fee renders "82,71" on screen and "82.70"
 * in the documents. Rounding here means every surface formats an already exact
 * value.
 */
export function roundCents(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Net, VAT and gross for an order, as one already-rounded set. */
export function orderFeeTotals(netTotal: number): {
  totalNet: number;
  vat: number;
  totalGross: number;
} {
  const totalNet = roundCents(netTotal);
  const vat = roundCents(totalNet * 0.19);
  // Summed from the rounded parts so the document foots.
  return { totalNet, vat, totalGross: roundCents(totalNet + vat) };
}
